/**
 * `<NoteLifecycleBadges>` — renders the D-007/D-008 lifecycle flags on a `NoteSummary`
 * (frontend.md §2.5).
 *
 * A `NoteSummary` carries `locallyModifiedAt` (D-007 dirty flag), `conflicted` (D-007),
 * and `deletedAt` (D-008 soft-delete). Result cards, the conflicted/soft-deleted tables,
 * and graph node inspectors all need to show these consistently — this is the shared
 * badge cluster.
 */

import type { NoteSummary } from '@/types';

interface NoteLifecycleBadgesProps {
  note: Pick<NoteSummary, 'locallyModifiedAt' | 'conflicted' | 'deletedAt'>;
}

export function NoteLifecycleBadges({ note }: NoteLifecycleBadgesProps): JSX.Element | null {
  const badges: JSX.Element[] = [];

  if (note.conflicted) {
    badges.push(
      <span key="conflicted" className="lifecycle-badge lifecycle-badge--conflicted">
        Conflicted
      </span>,
    );
  }
  if (note.deletedAt !== null) {
    badges.push(
      <span key="deleted" className="lifecycle-badge lifecycle-badge--deleted">
        Soft-deleted
      </span>,
    );
  }
  if (note.locallyModifiedAt !== null && !note.conflicted) {
    badges.push(
      <span key="dirty" className="lifecycle-badge lifecycle-badge--dirty">
        Local edits
      </span>,
    );
  }

  if (badges.length === 0) return null;
  return <span className="lifecycle-badges">{badges}</span>;
}
